type AdminAvatarSize = "sm" | "md" | "lg";

// Token-only palette — never raw hex. Background is a soft token tint so white
// initials sit on a calm surface; the matching ink token keeps contrast honest.
const PALETTE = [
  { bg: "var(--accent-soft)", fg: "var(--accent)" },
  { bg: "var(--warm-soft)", fg: "var(--warm)" },
  { bg: "var(--pass-soft)", fg: "var(--pass)" },
] as const;

const SIZES: Record<AdminAvatarSize, string> = {
  sm: "h-7 w-7 text-[11px]",
  md: "h-8 w-8 text-xs",
  lg: "h-[52px] w-[52px] text-lg",
};

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return words
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

function hash(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Deterministic initials avatar for admin tables/headers. Colour is picked
 * from a token-tint palette by a hash of `id ?? name`, so the same user always
 * renders the same chip. Pure presentational — no I/O.
 */
export function AdminAvatar({
  name,
  id,
  size = "md",
}: {
  name: string;
  id?: number | string;
  size?: AdminAvatarSize;
}) {
  const seed = String(id ?? name);
  // PALETTE is non-empty, so the modulo index is always in range.
  const tint = PALETTE[hash(seed) % PALETTE.length]!;
  return (
    <span
      aria-hidden="true"
      className={
        "grid place-items-center rounded-full font-mono-badge font-semibold leading-none shrink-0 " +
        SIZES[size]
      }
      style={{ background: tint.bg, color: tint.fg }}
    >
      {initials(name)}
    </span>
  );
}
